import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const SCORE_BUCKETS = Array.from({ length: 11 }, (_, index) => index);

const JobBarChart = ({ jobData = [], minScore = 0 }) => {
  const chartData = useMemo(() => {
    const buckets = new Map(SCORE_BUCKETS.map((bucket) => [bucket, 0]));

    jobData.forEach((job) => {
      const numericScore = Number(job?.score_user?.score);
      if (!Number.isFinite(numericScore)) {
        return;
      }

      const bucket = Math.max(0, Math.min(10, Math.round(numericScore)));
      buckets.set(bucket, (buckets.get(bucket) || 0) + 1);
    });

    const labels = SCORE_BUCKETS.map(String);
    return {
      labels,
      values: SCORE_BUCKETS.map((bucket) => buckets.get(bucket) || 0),
      colors: SCORE_BUCKETS.map((bucket) =>
        bucket >= minScore ? "rgba(255, 255, 255, 0.96)" : "rgba(255, 255, 255, 0.18)"
      ),
      borders: SCORE_BUCKETS.map((bucket) =>
        bucket >= minScore ? "rgba(255, 255, 255, 1)" : "rgba(255, 255, 255, 0.08)"
      ),
    };
  }, [jobData, minScore]);

  const data = useMemo(
    () => ({
      labels: chartData.labels,
      datasets: [
        {
          data: chartData.values,
          backgroundColor: chartData.colors,
          borderColor: chartData.borders,
          borderRadius: 999,
          borderSkipped: false,
          barPercentage: 0.58,
          categoryPercentage: 0.86,
          borderWidth: 1,
        },
      ],
    }),
    [chartData]
  );

  const options = useMemo(
    () => ({
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "#1F232C",
          borderColor: "rgba(59, 162, 166, 0.3)",
          borderWidth: 1,
          titleColor: "#FFFFFF",
          bodyColor: "#FFFFFF",
          displayColors: false,
          callbacks: {
            title: (context) => `Score ${context?.[0]?.label || ""}`,
            label: (context) => `${context.raw} jobs`,
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
          ticks: {
            color: "rgba(255, 255, 255, 0.55)",
            font: {
              size: 11,
              weight: "600",
            },
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(255, 255, 255, 0.08)",
            drawBorder: false,
          },
          border: {
            display: false,
          },
          ticks: {
            stepSize: 20,
            color: "rgba(255, 255, 255, 0.45)",
            font: {
              size: 11,
            },
          },
        },
      },
    }),
    []
  );

  return (
    <div className="bar-chart-container">
      <Bar data={data} options={options} />
    </div>
  );
};

export default JobBarChart;